const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function pad2(value) {
  return String(value).padStart(2, "0");
}

function formatIsoDate(date) {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function parseFlexibleBaseDate(value) {
  if (value == null || value === "") return null;

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  // Firestore Timestamp ou payload serializado { seconds/_seconds }.
  if (typeof value === "object") {
    if (typeof value.toDate === "function") {
      try {
        const fromTs = value.toDate();
        if (fromTs instanceof Date && !Number.isNaN(fromTs.getTime())) {
          return new Date(
            fromTs.getFullYear(),
            fromTs.getMonth(),
            fromTs.getDate(),
          );
        }
      } catch {
        // ignore
      }
    }
    const seconds = Number(value.seconds ?? value._seconds);
    if (Number.isFinite(seconds) && seconds > 0) {
      const fromSeconds = new Date(seconds * 1000);
      if (!Number.isNaN(fromSeconds.getTime())) {
        return new Date(
          fromSeconds.getFullYear(),
          fromSeconds.getMonth(),
          fromSeconds.getDate(),
        );
      }
    }
  }

  const raw = String(value).trim();
  if (!raw || raw === "[object Object]") return null;

  if (ISO_DATE_RE.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const brMatch = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (brMatch) {
    const day = Number(brMatch[1]);
    const month = Number(brMatch[2]);
    const year = Number(brMatch[3]);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function addDays(base, days) {
  const next = new Date(base);
  next.setDate(next.getDate() + days);
  return next;
}

export function parsePaymentConditionDays(description) {
  const normalized = String(description || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{M}+/gu, "");

  if (!normalized) return [];
  if (normalized.includes("vista")) return [0];

  const match = normalized.match(/(\d+(?:\s*\/\s*\d+)*)\s*dias?/i);
  if (!match?.[1]) return [];

  return match[1]
    .split("/")
    .map((part) => Number(String(part).trim()))
    .filter((value) => Number.isFinite(value) && value >= 0);
}

export function resolvePaymentConditionDescription(financial) {
  if (!financial) return null;

  const fromArray = Array.isArray(financial.paymentConditionDescriptions)
    ? financial.paymentConditionDescriptions
        .map((item) => String(item || "").trim())
        .filter(Boolean)
    : [];

  if (fromArray.length > 0) return fromArray[0];

  const legacy = String(financial.paymentConditionDescription || "").trim();
  return legacy || null;
}

export function computeDueDateFromPaymentCondition(description, baseDate) {
  const label = String(description || "").trim() || null;
  const base = parseFlexibleBaseDate(baseDate);
  const days = parsePaymentConditionDays(label || "");

  if (!base || days.length === 0) {
    return {
      dueDate: null,
      dueDateInstallments: [],
      paymentConditionLabel: label,
    };
  }

  const installments = days.map((dayCount) =>
    formatIsoDate(addDays(base, dayCount)),
  );

  return {
    dueDate: installments[0] ?? null,
    dueDateInstallments: installments,
    paymentConditionLabel: label,
  };
}

export function normalizeFreightIssueDate(createData, initialDate) {
  const primary = parseFlexibleBaseDate(createData);
  if (primary) return formatIsoDate(primary);

  const fallback = parseFlexibleBaseDate(initialDate);
  return fallback ? formatIsoDate(fallback) : null;
}

export function formatDatePtBr(value) {
  const parsed = parseFlexibleBaseDate(value);
  if (!parsed) return "—";
  return `${pad2(parsed.getDate())}/${pad2(parsed.getMonth() + 1)}/${parsed.getFullYear()}`;
}

export function parseDateToTimestamp(value) {
  const parsed = parseFlexibleBaseDate(value);
  return parsed ? parsed.getTime() : 0;
}
